// TW-3220 — Amplitude events of the Univers Métier (WP-delegated) pages. The
// download buttons and the paywall modal are WP theme markup, so we listen at
// document level (capture phase, before the theme's own handlers) instead of
// binding the elements: it survives Turbo body swaps without re-binding.
// `category` = first path segment, same rule as Wordpress::UniversMetierTracking.
import Tracking from './tracking';

// The main buttons carry the ids; the sticky bar shown on scroll repeats the link without one.
const DOWNLOAD_SELECTOR = [
  '#nt-livret-download',
  '#nt-livret-sticky a[href]',
  '#nt-atelier-cta-btn',
  '#nt-atelier-sticky a[href]',
].join(', ');
const UNLOCK_MODAL_ID = 'nt-unlock-modal';
// In memory, not a DOM attribute: Turbo caches these pages, and a restored snapshot clones the
// attributes of the modal but not its observer.
const observedModals = new WeakSet();

// Query string dropped (utm_*, auth params) so a page groups under one URL.
function universMetierPageProperties() {
  const { origin, pathname } = window.location;

  return { category: pathname.split('/').filter(Boolean)[0], url: `${origin}${pathname}` };
}

// The paywall only offers the close controls and a link to a landing.
function paywallCta(target) {
  if (target.closest('[data-nt-unlock-close]')) return 'dismiss';

  return target.closest('a[href]') ? 'read-more' : null;
}

function trackUniversMetierClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const download = target.closest(DOWNLOAD_SELECTOR);
  if (download) {
    Tracking.amplitudeTracking('document.downloaded', { ...universMetierPageProperties(), 'file-url': download.href });
    return;
  }

  if (!target.closest(`#${UNLOCK_MODAL_ID}`)) return;

  const cta = paywallCta(target);
  if (cta) Tracking.amplitudeTracking('paywall.univers.metier.clicked', { ...universMetierPageProperties(), cta });
}

// The theme shows the modal by removing its `hidden` attribute: track each
// hidden → visible transition (one exposure per opening).
function observeUnlockModal() {
  const modal = document.getElementById(UNLOCK_MODAL_ID);
  if (!modal || observedModals.has(modal)) return;

  observedModals.add(modal);

  const trackShown = () => Tracking.amplitudeTracking('paywall.univers.metier.saw', universMetierPageProperties());
  let visible = !modal.hidden;
  if (visible) trackShown();

  new MutationObserver(() => {
    if (!modal.hidden && !visible) trackShown();
    visible = !modal.hidden;
  }).observe(modal, { attributes: true, attributeFilter: ['hidden'] });
}

document.addEventListener('click', trackUniversMetierClick, true);
document.addEventListener('turbo:load', observeUnlockModal);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeUnlockModal);
} else {
  observeUnlockModal();
}

export { universMetierPageProperties, trackUniversMetierClick, observeUnlockModal };
